"use client";

import { useEffect } from "react";

/**
 * Suppresses expected browser errors that occur during sleep/wake cycles
 * and other network disruptions
 */
export function ErrorSuppressor() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Track when we're likely waking from sleep
    let recentErrorCount = 0;
    let lastErrorTime = 0;

    function updateErrorRate(
      lastTime: number,
      count: number,
      windowMs = 5000,
      threshold = 2
    ): { suppress: boolean; lastTime: number; count: number } {
      const now = Date.now();
      const newCount = now - lastTime < windowMs ? count + 1 : 1;
      return { suppress: newCount > threshold, lastTime: now, count: newCount };
    }

    const shouldSuppressError = (message: string): boolean => {
      const isOffline = typeof navigator !== "undefined" && !navigator.onLine;

      // CoreLocation errors (geolocation API issues)
      if (
        message.includes("CoreLocationProvider") ||
        message.includes("kCLErrorLocationUnknown")
      ) {
        return true;
      }

      // Wake lock errors - these are expected when page is not visible
      if (
        message.includes("WakeLock") &&
        (message.includes("NotAllowedError") ||
          message.includes("Failed to execute 'request' on 'WakeLock'"))
      ) {
        return true;
      }

      // Audio playback errors - expected when browser blocks autoplay (second tab/browser, no user interaction)
      if (
        (message.includes("[AudioManager]") || message.includes("[useAssistantAudio]")) &&
        message.includes("NotAllowedError")
      ) {
        return true;
      }

      // ClerkJS network errors - suppress when offline
      if (
        message.includes("ClerkJS") &&
        (message.includes("Network error") ||
          message.includes("Failed to fetch") ||
          message.includes("clerk.thesukha.co") ||
          message.includes("clerk.com"))
      ) {
        // Always suppress Clerk errors when offline, otherwise only if multiple
        if (isOffline) {
          return true;
        }
        const result = updateErrorRate(lastErrorTime, recentErrorCount);
        lastErrorTime = result.lastTime;
        recentErrorCount = result.count;
        return result.suppress;
      }

      // Network errors during sleep/wake or when offline
      if (
        message.includes("ERR_NETWORK_IO_SUSPENDED") ||
        message.includes("ERR_SOCKET_NOT_CONNECTED") ||
        message.includes("Failed to load resource") ||
        message.includes("net::ERR_") ||
        message.includes("ERR_INTERNET_DISCONNECTED") ||
        message.includes("ERR_ADDRESS_UNREACHABLE")
      ) {
        // Always suppress when offline
        if (isOffline) {
          return true;
        }
        const result = updateErrorRate(lastErrorTime, recentErrorCount);
        lastErrorTime = result.lastTime;
        recentErrorCount = result.count;
        return result.suppress;
      }

      // Realtime subscription errors during network issues
      // Suppress 403 errors when offline (heartbeat failures)
      if (message.includes("pndsn.com")) {
        if (
          isOffline ||
          message.includes("ERR_NETWORK_IO_SUSPENDED") ||
          message.includes("ERR_SOCKET_NOT_CONNECTED") ||
          message.includes("403") ||
          message.includes("Forbidden")
        ) {
          return true;
        }
      }

      // API endpoint failures when offline
      if (
        isOffline &&
        (message.includes("/api/v1/") ||
          message.includes("Failed to fetch") ||
          message.includes("TypeError: Failed to fetch"))
      ) {
        return true;
      }

      // Firefox reader mode API errors - harmless browser detection failures
      if (
        message.includes("__firefox__") ||
        message.includes("window.__firefox__.reader") ||
        message.includes("undefined is not an object (evaluating 'window.__firefox__.reader')")
      ) {
        return true;
      }

      return false;
    };

    // Helper to extract error message from various formats
    const extractErrorMessage = (args: unknown[]): string => {
      return args
        .map((arg) => {
          if (typeof arg === "string") return arg;
          if (arg instanceof Error) {
            return `${arg.message} ${arg.stack || ""}`;
          }
          try {
            return JSON.stringify(arg);
          } catch {
            return String(arg);
          }
        })
        .join(" ");
    };

    // Override console.error to suppress specific errors
    const originalConsoleError = console.error;
    console.error = (...args: unknown[]) => {
      const message = extractErrorMessage(args);

      if (shouldSuppressError(message)) {
        return;
      }

      originalConsoleError.apply(console, args);
    };

    // Override console.warn to suppress network warnings when offline
    const originalConsoleWarn = console.warn;
    console.warn = (...args: unknown[]) => {
      const message = extractErrorMessage(args);

      if (shouldSuppressError(message)) {
        return;
      }

      originalConsoleWarn.apply(console, args);
    };

    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const message =
        event.reason instanceof Error
          ? `${event.reason.message} ${event.reason.stack || ""}`
          : String(event.reason);

      if (shouldSuppressError(message)) {
        event.preventDefault();
      }
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    // Handle window errors
    const handleWindowError = (event: ErrorEvent) => {
      if (shouldSuppressError(event.message)) {
        event.preventDefault();
        return true;
      }
      return false;
    };

    window.addEventListener("error", handleWindowError);

    return () => {
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
      window.removeEventListener("error", handleWindowError);
    };
  }, []);

  return null;
}
